import { useCallback, useContext } from 'react';

import { useCookieConsent, logger } from '@packages/utilities';

import { TrackingContext } from '../../components/TrackingProvider';

const dispatchMouseflowTag = ({
  tagName,
  mouseflowQueueName,
}: {
  tagName: string;
  mouseflowQueueName: string;
}) => {
  if (typeof window === 'undefined') return;

  const queue = (window as { [key: string]: any })[mouseflowQueueName];

  if (queue) {
    queue.push(['tag', tagName]);
  } else {
    logger.warn(
      `[useMouseflow] window.${mouseflowQueueName} is not defined, could not push tag`,
      tagName,
    );
  }
};

/**
 * Coookie consent dependent hook to push mouseflow tracking data.
 *
 * @param tagName the mouseflow tagname
 */
export const useMouseflow = (): ((tagName: string) => void) => {
  const context = useContext(TrackingContext);
  const mouseflowQueueName = context?.mouseflowQueueName;

  if (context === null || typeof mouseflowQueueName === 'undefined') {
    logger.error(
      '[useMouseflow]: hook was used outside of a TrackingProvider component. Events might not be tracked correctly.',
    );
  } else if (
    typeof window !== 'undefined' &&
    !(window as { [key: string]: any })[mouseflowQueueName]
  ) {
    (window as { [key: string]: any })[mouseflowQueueName] = [];
  }

  const isConsentGiven = useCookieConsent('C0003');

  return useCallback(
    (tagName: string) => {
      if (!isConsentGiven || !mouseflowQueueName) return;

      dispatchMouseflowTag({ tagName, mouseflowQueueName });
    },
    [isConsentGiven, mouseflowQueueName],
  );
};
